import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import React, { useEffect, useState } from 'react';
import LoginForm from './components/LoginForm';
import PatientForm from './components/PatientForm';
import PatientsTable from './components/PatientsTable';
import SimpleLayout from './components/SimpleLayout';

const ROLES = {
  PATIENT: 'patient',
  OPTOMETRIST: 'optometrist',
  ADMIN: 'admin'
};

const CREDENTIALS = {
  '1234': { role: ROLES.OPTOMETRIST, campId: '1234' },
  '9999': { role: ROLES.ADMIN, campId: '9999' }
};

const theme = createTheme({
  palette: {
    primary: {
      main: '#1e3a8a',
      light: '#3b82f6',
      dark: '#1e40af',
    },
    secondary: {
      main: '#3b82f6',
      light: '#60a5fa',
      dark: '#1d4ed8',
    },
    background: {
      default: '#f8fafc',
      paper: '#ffffff',
    },
    text: {
      primary: '#1f2937',
      secondary: '#6b7280',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h4: {
      fontWeight: 700,
    },
    h6: {
      fontWeight: 600,
    },
  },
  shape: {
    borderRadius: 5,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          borderRadius: 5,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 5,
          },
        },
      },
    },
  },
});

function App() {
  const [currentRole, setCurrentRole] = useState(ROLES.OPTOMETRIST); // Default to show login form
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loginAttempts, setLoginAttempts] = useState(0);
  const [patients, setPatients] = useState([]);
  const [editingLeadId, setEditingLeadId] = useState(null);

  // Load patients from localStorage on component mount
  useEffect(() => {
    const savedPatients = localStorage.getItem('campPatients');
    if (savedPatients) {
      setPatients(JSON.parse(savedPatients));
    }

    // Check URL for patient form access
    if (window.location.pathname === '/patient') {
      setCurrentRole(ROLES.PATIENT);
    }
  }, []);

  // Save patients to localStorage whenever patients state changes
  useEffect(() => {
    localStorage.setItem('campPatients', JSON.stringify(patients));
  }, [patients]);

  const handleLogin = (credentials) => {
    const { campId, passcode, campData } = credentials;

    // If we have campData from API, use that for authentication
    if (campData) {
      // Determine role based on camp data
      const role = campData.current_user_role === 'Roll_1' ? ROLES.OPTOMETRIST : ROLES.ADMIN;
      setCurrentRole(role);
      setIsAuthenticated(true);
      setLoginAttempts(0);

      // Role-based content is handled in renderContent()

      // Store additional camp information for later use
      localStorage.setItem('currentCamp', JSON.stringify({
        campId: campData.camp_id,
        campName: campData.name,
        startDate: campData.camp_start_date,
        endDate: campData.camp_end_date,
        userRole: campData.current_user_role
      }));
    } else {
      // Fallback to hardcoded credentials for demo
      if (CREDENTIALS[passcode] && CREDENTIALS[passcode].campId === campId) {
        setCurrentRole(CREDENTIALS[passcode].role);
        setIsAuthenticated(true);
        setLoginAttempts(0);
      } else {
        setLoginAttempts(prev => prev + 1);
        if (loginAttempts >= 2) {
          alert('Too many incorrect attempts. Please refresh the page.');
          setLoginAttempts(0);
        }
      }
    }
  };

  const handleLogout = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      await fetch('https://camps.asgeyehospital.com/asg-camps-backend/auth/logout', {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'x-asg-camp-session-token': token
        }
      }).catch(() => { });
    } catch (_) {
      // ignore network errors during logout
    }

    setCurrentRole(ROLES.OPTOMETRIST);
    setIsAuthenticated(false);
    setLoginAttempts(0);

    localStorage.removeItem('accessToken');
    localStorage.removeItem('campData');
    localStorage.removeItem('currentCamp');
  };

  const handlePatientSubmit = (patientData) => {
    const newPatient = {
      id: Date.now().toString(),
      ...patientData,
      registrationDate: new Date().toISOString(),
      status: 'pending',
      diagnoses: [],
      remarks: ''
    };
    setPatients(prev => [...prev, newPatient]);
  };



  const handleOpenLeadForEdit = (leadId) => {
    setEditingLeadId(leadId);
  };

  const renderContent = () => {
    if (!isAuthenticated) {
      return <LoginForm onSubmit={handleLogin} attempts={loginAttempts} />;
    }

    // Get user role from localStorage
    const campData = localStorage.getItem('currentCamp');
    let userRole = currentRole;
    if (campData) {
      const camp = JSON.parse(campData);
      userRole = camp.userRole;
    }

    // Handle edit lead mode
    if (editingLeadId) {
      return (
        <PatientForm
          leadId={editingLeadId}
          mode="edit"
          onBackToList={() => setEditingLeadId(null)}
        />
      );
    }

    // Role-based content display
    if (userRole === 'Roll_1') {
      return <PatientForm onSubmit={handlePatientSubmit} />;
    } else if (userRole === 'Roll_2') {
      return (
        <PatientsTable
          currentRole={currentRole}
          onOpenLead={handleOpenLeadForEdit}
          onAuthExpired={() => {
            setIsAuthenticated(false);
            setCurrentRole(ROLES.OPTOMETRIST);
            localStorage.removeItem('accessToken');
            localStorage.removeItem('campData');
            localStorage.removeItem('currentCamp');
          }}
        />
      );
    }

    // Default fallback
    return <PatientForm onSubmit={handlePatientSubmit} />;
  };

  // Show login form first, then patient form for public access
  if (!isAuthenticated) {
    if (currentRole === ROLES.PATIENT) {
      return (
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <PatientForm onSubmit={handlePatientSubmit} />
        </ThemeProvider>
      );
    } else {
      return (
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <LoginForm onSubmit={handleLogin} attempts={loginAttempts} />
        </ThemeProvider>
      );
    }
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <SimpleLayout onLogout={handleLogout}>
        {renderContent()}
      </SimpleLayout>
    </ThemeProvider>
  );
}

export default App;